'use client';

import React, { useState, useEffect } from 'react';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import StoryCard from './StoryCard';

import FilterBar from './FilterBar';
import StatsSection from './StatsSection';
import StoryModal from './StoryModal';

interface Story {
  id: number;
  name: string;
  age: number;
  program: string;
  achievement: string;
  duration: string;
  image: string;
  alt: string;
  quote: string;
  fullStory: string;
  category: string;
  ageGroup: string;
  metrics: {
    label: string;
    value: string;
  }[];
  milestones: {
    month: string;
    achievement: string;
  }[];
}

interface Transformation {
  id: number;
  name: string;
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
  duration: string;
  program: string;
  category: string;
  ageGroup: string;
}

const SuccessStoriesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All Stories');
  const [selectedProgram, setSelectedProgram] = useState('All Programs');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('All Ages');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const stats = [
  { icon: 'UserGroupIcon', value: '1000+', label: 'Success Stories' },
  { icon: 'TrophyIcon', value: '2000+', label: 'Athletes Trained' },
  { icon: 'StarIcon', value: '4.9/5', label: 'Average Rating' },
  { icon: 'ChartBarIcon', value: '95%', label: 'Goal Achievement' }];


  const mockStories: Story[] = [
  {
    id: 11,
    name: 'Debashree Majumdar',
    age: 27,
    program: 'Speed Training',
    achievement: 'Rio 2016 Olympian & Asian Games Gold Medalist',
    duration: '48 months',
    image: "/assets/images/image-2-1770480683644.png",
    alt: 'Debashree Majumdar with legendary sprinter Usain Bolt at international athletics event',
    quote: 'GO Speed Academy helped me achieve my dream of representing India at the Olympics and winning gold at the Asian Games.',
    fullStory: `My journey with GO Speed Academy has been extraordinary. As an aspiring athlete with Olympic dreams, I needed world-class training that could prepare me for the highest level of international competition.\n\nThe coaches at GO Speed created an intensive training program focused on explosive speed, endurance, technique refinement, and mental preparation. Every training session was designed to push me closer to my Olympic dream.\n\nThe breakthrough came when I qualified to represent India at the Rio 2016 Olympics. Standing on that Olympic stage, competing against the world's best athletes, was a dream come true. The experience taught me that with dedication and proper guidance, anything is possible.\n\nBut my journey didn't stop there. I continued training with the same intensity and focus. At the Asian Games, all the hard work paid off when I won the gold medal. Standing on that podium with the Indian flag rising and the national anthem playing was one of the proudest moments of my life.\n\nDuring my journey, I had the incredible opportunity to meet and train alongside legendary athletes like Usain Bolt. These experiences motivated me to push even harder and represent India with pride on the world stage.\n\nGO Speed Academy didn't just train my body - they trained my mind to compete at the Olympic level and believe that Indian athletes can stand shoulder to shoulder with the world's best.`,
    category: 'Athletes',
    ageGroup: 'Adults (18-54)',
    metrics: [
    { label: 'Olympic Games', value: 'Rio 2016' },
    { label: 'Asian Games Gold', value: '1' },
    { label: 'International Events', value: '15+' },
    { label: 'Years Training', value: '4' }],
    milestones: [
    { month: 'Month 12', achievement: 'Qualified for national team' },
    { month: 'Month 24', achievement: 'Won national championship' },
    { month: 'Month 36', achievement: 'Qualified for Rio 2016 Olympics' },
    { month: 'Month 40', achievement: 'Competed at Rio 2016 Olympics' },
    { month: 'Month 44', achievement: 'Won gold medal at Asian Games' },
    { month: 'Month 48', achievement: 'Met and trained with Usain Bolt' }]
  },
  {
    id: 7,
    name: 'Narayan Thakur',
    age: 28,
    program: 'Speed Training',
    achievement: 'Asian Games Medalist & National Para Athletics Champion',
    duration: '36 months',
    image: "/assets/images/PHOTO-2025-01-26-21-22-24-1769023479988.jpg",
    alt: 'Narayan Thakur standing on podium at medal ceremony with gold medal around neck',
    quote: 'GO Speed Academy helped me achieve my dream of representing India at the Asian Games and becoming a national champion.',
    fullStory: `My journey with GO Speed Academy has been transformative. As a para-athlete, I needed specialized training that understood both my potential and my unique requirements.\n\nThe coaches at GO Speed created a comprehensive training program focused on explosive speed, technique refinement, and mental conditioning. Every session was designed to push my limits while respecting my body's needs.\n\nThe breakthrough came at the 2018 Asian Games when I won the gold medal in the 100 meters. Standing on that podium, hearing the national anthem, was the proudest moment of my life.\n\nBut I didn't stop there. The training continued, and at the 2022 Asian Games, I added two bronze medals in the 100 and 200 meters to my collection.\n\nFrom 2021 to 2023, I dominated the national circuit, becoming the National Para Athletics Champion in both the 100 and 200 meters for three consecutive years.\n\nGO Speed Academy didn't just train my body - they trained my mind to believe that anything is possible with dedication, proper guidance, and relentless effort.`,
    category: 'Athletes',
    ageGroup: 'Adults (18-54)',
    metrics: [
    { label: 'Asian Games Gold', value: '1' },
    { label: 'Asian Games Bronze', value: '2' },
    { label: 'National Titles', value: '6' },
    { label: 'Years Champion', value: '3' }],

    milestones: [
    { month: 'Month 12', achievement: 'Qualified for Asian Games 2018' },
    { month: 'Month 18', achievement: 'Won gold medal in 100m at 2018 Asian Games' },
    { month: 'Month 24', achievement: 'Became National Champion in 100m and 200m (2021)' },
    { month: 'Month 30', achievement: 'Defended national titles (2022)' },
    { month: 'Month 32', achievement: 'Won two bronze medals at 2022 Asian Games' },
    { month: 'Month 36', achievement: 'Third consecutive national championship (2023)' }]

  },
  {
    id: 8,
    name: 'Shreyansh Trivedi',
    age: 26,
    program: 'Speed Training',
    achievement: '2023 Asian Para Games Medalist & World Championship Finalist',
    duration: '30 months',
    image: "/assets/images/PHOTO-2025-01-26-21-38-33-1769029373907.jpg",
    alt: 'Shreyansh Trivedi with PM Modi presenting achievements at official ceremony',
    quote: 'GO Speed Academy helped me reach the world stage and bring pride to India at international para athletics competitions.',
    fullStory: `My journey with GO Speed Academy has been nothing short of extraordinary. As a para-athlete competing in the T37 category, I needed specialized training that could help me compete at the highest international level.\n\nThe coaches at GO Speed understood my goals and created a rigorous training program focused on explosive speed, endurance, and race strategy. Every training session was designed to push me closer to my dream of representing India on the world stage.\n\nThe breakthrough came at the 2023 Asian Para Games where I won two bronze medals. Standing on that podium with the Indian flag behind me was an incredible moment that validated all the hard work and dedication.\n\nBut the journey didn't stop there. I went on to compete at the World Para Athletics Championship in Paris, where I secured a top 6 position against the world's best athletes. Competing at that level showed me what's possible with the right training and mindset.\n\nI also became the national record holder in both the 100m and 200m events in the T37 category. These records represent not just my personal achievement, but also the effectiveness of GO Speed Academy's training methodology.\n\nGO Speed Academy didn't just train my body - they trained my mind to compete at the highest level and represent India with pride on the international stage.`,
    category: 'Athletes',
    ageGroup: 'Adults (18-54)',
    metrics: [
    { label: 'Asian Para Games Bronze', value: '2' },
    { label: 'World Championship Rank', value: 'Top 6' },
    { label: 'National Records', value: '2' },
    { label: 'Events', value: '100m & 200m' }],

    milestones: [
    { month: 'Month 10', achievement: 'Qualified for international competitions' },
    { month: 'Month 18', achievement: 'Set national record in 100m T37 category' },
    { month: 'Month 22', achievement: 'Set national record in 200m T37 category' },
    { month: 'Month 26', achievement: 'Won two bronze medals at 2023 Asian Para Games' },
    { month: 'Month 30', achievement: 'Secured top 6 position at World Para Athletics Championship in Paris' }]

  },
  {
    id: 9,
    name: 'Vihaan Khanna',
    age: 16,
    program: 'Speed Training',
    achievement: 'Multiple National & State Championships Medalist',
    duration: '24 months',
    image: "/assets/images/WhatsApp_Image_2026-01-22_at_02.41.55-1769029937187.jpeg",
    alt: 'Vihaan Khanna at medal ceremony at Delhi State Open Athletics Championships 2025',
    quote: 'GO Speed Academy helped me achieve multiple medals at national and state level competitions, and set state records in swimming.',
    fullStory: `My journey with GO Speed Academy has been incredible. As a young athlete with big dreams, I needed specialized training that could help me compete at the highest levels in triathlon and swimming.\n\nThe coaches at GO Speed created a comprehensive training program that focused on building my speed, endurance, and technique across multiple disciplines. The structured approach helped me excel in both swimming and athletics.\n\nMy breakthrough came when I won the bronze medal at the Junior National Athletic Championship in Triathlon-A. This was a defining moment that showed me what's possible with dedicated training and expert guidance.\n\nI continued to build on this success, winning another bronze medal at the SGFI games. The training methodology at GO Speed Academy prepared me not just physically, but mentally for high-pressure competitions.\n\nAt the Delhi State Championships, I dominated my events, becoming a three-time double gold medalist. The consistency in my performance showed the effectiveness of the training program.\n\nI also became the Delhi State record holder in both the 50m and 200m swimming events. These records represent years of hard work and the scientific training approach at GO Speed Academy.\n\nMost recently, I added a bronze medal at the North Zone Championships to my collection. Each achievement has been a stepping stone, and GO Speed Academy has been with me every step of the way, helping me reach new heights in my athletic career.`,
    category: 'Athletes',
    ageGroup: 'Teens (13-17)',
    metrics: [
    { label: 'National Bronze', value: '1' },
    { label: 'SGFI Bronze', value: '1' },
    { label: 'State Gold Medals', value: '6' },
    { label: 'State Records', value: '2' }],

    milestones: [
    { month: 'Month 8', achievement: 'Won bronze at Junior National Athletic Championship in Triathlon-A' },
    { month: 'Month 12', achievement: 'Won bronze medal at SGFI games' },
    { month: 'Month 16', achievement: 'First double gold at Delhi State Championships' },
    { month: 'Month 18', achievement: 'Set Delhi State record in 50m event' },
    { month: 'Month 20', achievement: 'Set Delhi State record in 200m event' },
    { month: 'Month 22', achievement: 'Three-time double gold medalist at Delhi State Championships' },
    { month: 'Month 24', achievement: 'Won bronze medal at North Zone Championships' }]

  },
  {
    id: 10,
    name: 'Amieyra Khosla',
    age: 13,
    program: 'Speed Training',
    achievement: 'SGFI Medalist & Delhi State Triathlon Champion',
    duration: '20 months',
    image: "/assets/images/PHOTO-2025-01-26-22-24-57-1769030269858.jpg",
    alt: 'Amieyra Khosla at 35th North Zone Junior Athletics Championships 2024',
    quote: 'GO Speed Academy helped me excel in both athletics and triathlon, winning medals at state and national level competitions.',
    fullStory: `My journey with GO Speed Academy has been transformative. As a young athlete with dreams of competing at the highest levels, I needed specialized training that could help me excel in multiple disciplines.\n\nThe coaches at GO Speed created a comprehensive training program that focused on building my speed, endurance, and technique. The structured approach helped me develop the versatility needed to compete in both athletics and triathlon events.\n\nMy breakthrough came when I won the silver medal in the 100 meters at the SGFI (School Games Federation of India) under 14 championships. This was a defining moment that showed me what's possible with dedicated training and expert guidance.\n\nI continued to build on this success at the North Zone Under 14 championships, where I performed exceptionally well in triathlon events. The multi-disciplinary training at GO Speed Academy prepared me perfectly for the demands of triathlon competition.\n\nThe highlight of my journey so far has been winning the gold medal in triathlon at the Delhi State Championships. Standing on that podium with the gold medal was an incredible feeling that validated all the hard work and early morning training sessions.\n\nGO Speed Academy didn't just train my body - they trained my mind to compete with confidence and handle the pressure of high-level competitions. Each achievement has been a stepping stone, and I'm excited to see where this journey takes me next.`,
    category: 'Athletes',
    ageGroup: 'Teens (13-17)',
    metrics: [
    { label: 'SGFI Silver', value: '1' },
    { label: 'State Gold', value: '1' },
    { label: 'North Zone', value: 'Top Performer' },
    { label: 'Events', value: '100m & Triathlon' }],
    milestones: [
    { month: 'Month 6', achievement: 'Qualified for SGFI under 14 championships' },
    { month: 'Month 10', achievement: 'Won silver medal in 100m at SGFI championships' },
    { month: 'Month 14', achievement: 'Competed at North Zone Under 14 championships' },
    { month: 'Month 16', achievement: 'Excelled in triathlon at North Zone championships' },
    { month: 'Month 18', achievement: 'Qualified for Delhi State Championships' },
    { month: 'Month 20', achievement: 'Won gold medal in triathlon at Delhi State Championships' }]
  }];


  const filterContent = (item: Story) => {
    const categoryMatch = selectedCategory === 'All Stories' || item.category === selectedCategory;
    const programMatch = selectedProgram === 'All Programs' || item.program === selectedProgram;
    const ageGroupMatch = selectedAgeGroup === 'All Ages' || item.ageGroup === selectedAgeGroup;
    const searchMatch = searchQuery === '' || 'name' in item && item.name.toLowerCase().includes(searchQuery.toLowerCase());

    return categoryMatch && programMatch && ageGroupMatch && searchMatch;
  };

  const filteredStories = mockStories.filter(filterContent);

  const handleViewStory = (story: Story) => {
    setSelectedStory(story);
    setIsStoryModalOpen(true);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-12 bg-muted rounded w-1/3 mb-4"></div>
              <div className="h-6 bg-muted rounded w-2/3 mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                <div key={i} className="h-96 bg-muted rounded-lg"></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4">
              Success Stories
            </h1>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Real transformations, real results. Discover how GO Speed Academy has helped athletes, families, and individuals achieve their fitness goals through structured training and expert guidance.
            </p>
          </div>

          <StatsSection stats={stats} />

          {/* Achievement Gallery Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6 flex items-center">
              <Icon name="PhotoIcon" size={24} variant="outline" className="mr-2 text-accent" />
              Achievement Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/236aed7f-de15-411e-912e-7df8bdbb43b0-1770481514831.png"
                  alt="Group of GO Speed Academy athletes proudly displaying their medals at sports field after winning competitions"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Our Champions with Their Medals
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/1eec4fb5-b733-4da3-83d3-68d3fbd2c04f-1770482064263.png"
                  alt="Group of GO Speed Academy athletes with medals standing together at outdoor sports field celebrating their achievements"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    International & National Achievers
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/tempImagesUMsCM-2-3-1770482384470.jpg"
                  alt="Coach speaking to group of young athletes during training session at GO Speed Academy facility"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Expert Coaching & Training Sessions
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/c252966b-11bd-485c-b0c2-2389f57e0dd5-1770482442846.JPG"
                  alt="Coach Amit Khanna with group of young medal-winning athletes at stadium after competition"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Coach with Medal Winners
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/PHOTO-2024-11-16-20-03-55_3-1770482617299.jpg"
                  alt="Medal winners standing on podium at 33rd Delhi State Annual Athletics Championship with certificates and medals"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    33rd Delhi State Athletics Championship Podium
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/PHOTO-2024-11-16-20-17-47-1770482999008.jpg"
                  alt="Group photo of young athletes with coaches at training field during outdoor practice session"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Training Field Group Session
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/PHOTO-2025-01-26-22-28-24-1770483281376.jpg"
                  alt="Young athletes from Theshri Ram School standing on podium with medals at competition, holding yellow school banner showing second place achievement"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Theshri Ram School Podium Achievement
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/PHOTO-2025-01-25-23-03-39-1770483669734.jpg"
                  alt="Indian team athletes in blue Adidas track suits with medals at Hangzhou Asian Para Games, standing together in front of official event banner"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Hangzhou Asian Para Games Medalists
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/tempImageMY5t2t-2-1770538531521.jpg"
                  alt="Young athlete holding certificate of appreciation with gold medal standing between two coaches on red carpet at award ceremony"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Certificate Presentation with Coaches
                  </p>
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden shadow-card hover:shadow-hover transition-all duration-300 transform hover:-translate-y-1">
                <AppImage
                  src="/assets/images/PHOTO-2026-02-17-trophy-ceremony-1739800130000.jpg"
                  alt="Coaches and athletes holding golden trophies at outdoor competition venue with blue tents and spectators in background"
                  className="w-full h-64 object-cover" />

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/90 to-transparent p-4">
                  <p className="text-primary-foreground font-heading font-semibold text-sm">
                    Trophy Ceremony at Competition Venue
                  </p>
                </div>
              </div>
            </div>
          </div>

          <FilterBar
            selectedCategory={selectedCategory}
            selectedProgram={selectedProgram}
            selectedAgeGroup={selectedAgeGroup}
            searchQuery={searchQuery}
            onCategoryChange={setSelectedCategory}
            onProgramChange={setSelectedProgram}
            onAgeGroupChange={setSelectedAgeGroup}
            onSearchChange={setSearchQuery} />


          {filteredStories.length > 0 &&
          <div className="mb-12">
              <h2 className="text-2xl font-heading font-bold text-primary mb-6 flex items-center">
                <Icon name="UserGroupIcon" size={24} variant="outline" className="mr-2 text-accent" />
                International & National Achievers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStories.map((story) =>
              <StoryCard
                key={story.id}
                story={story}
                onViewDetails={() => handleViewStory(story)} />

              )}
              </div>
            </div>
          }

          {filteredStories.length === 0 &&
          <div className="text-center py-16">
              <Icon name="MagnifyingGlassIcon" size={64} variant="outline" className="mx-auto text-text-secondary mb-4" />
              <h3 className="text-xl font-heading font-bold text-primary mb-2">
                No Results Found
              </h3>
              <p className="text-text-secondary mb-6">
                Try adjusting your filters or search query to find more success stories.
              </p>
              <button
              onClick={() => {
                setSelectedCategory('All Stories');
                setSelectedProgram('All Programs');
                setSelectedAgeGroup('All Ages');
                setSearchQuery('');
              }}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-accent text-accent-foreground font-heading font-bold rounded-md shadow-card hover:shadow-hover hover:bg-accent/90 transition-all duration-300 transform hover:-translate-y-0.5">

                Reset Filters
              </button>
            </div>
          }

          <div className="mt-16 bg-accent/10 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
              Join hundreds of satisfied members who have transformed their lives through structured training and expert guidance at GO Speed Academy.
            </p>
            <a
              href="https://forms.gle/9yvnUPvV2ifJin4S9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-accent text-accent-foreground font-heading font-bold rounded-md shadow-card hover:shadow-hover hover:bg-accent/90 transition-all duration-300 transform hover:-translate-y-0.5">

              <span>Book Your Trial Session</span>
              <Icon name="ArrowRightIcon" size={20} variant="outline" />
            </a>
          </div>
        </div>
      </div>

      <StoryModal
        isOpen={isStoryModalOpen}
        onClose={() => setIsStoryModalOpen(false)}
        story={selectedStory} />

    </div>);

};

export default SuccessStoriesInteractive;